import type { ComponentType } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import ROUTES from '../shared/constants/routes';
import { invoicingV2Routes } from '../features/invoicing/v2/routes';
import { publicQrRoutes } from '../features/invoicing/qr/routes';
import { InvoicingLayout } from '../features/invoicing/v2/views/InvoicingLayout';
import { LegacyInvoicingRedirect } from '../features/invoicing/LegacyInvoicingRedirect';
import { onboardingV3Routes } from '../features/onboarding-v3/host/routes';
import { satIdRoutes } from '../features/sat-id/routes/satId.routes';
import ErrorPage from '../pages/errorPage/ErrorPage';
import { fiscalAdvisorRoutes } from '@/features/fiscal-advisor/routes';
import { declarationsRoutes } from '@/features/declarations-new/routes/declarationsNew.routes';
import { planSectionLoader } from '../pages/plansAndPricesV2/planSections';
import { legacyAllyDeeplinkLoader } from '../features/onboarding/lib/legacyAllyDeeplink';
import { devTestRoutes } from './DevTest.routes';
import BootSplash from '../shared/components/BootSplash';

const lazyView =
	(load: () => Promise<{ default: ComponentType }>) => async () => ({
		Component: (await load()).default,
	});

const router = createBrowserRouter([
	{
		id: 'home',
		path: '/',
		errorElement: <ErrorPage />,
		hydrateFallbackElement: <BootSplash />,
		// The authenticated shell (ProtectedRoutes → MainLayout, SAT providers,
		// bootstrap queries) is the bulk of the app graph; lazy-load it so it
		// stays out of the entry chunk and off the anonymous /login path. The
		// BootSplash hydrate fallback covers the chunk download.
		lazy: async () => {
			const { default: ProtectedRoutes } = await import('./Protected.routes');
			return {
				element: (
					<NuqsAdapter>
						<ProtectedRoutes redirectPath='/login' />
					</NuqsAdapter>
				),
			};
		},
		children: [
			{
				id: 'root',
				path: ROUTES.ROOT,
				lazy: lazyView(() => import('../pages/home')),
			},
			{
				id: 'promo',
				path: ROUTES.PROMO,
				lazy: lazyView(() => import('../pages/promo')),
			},
			{
				id: 'userProfile',
				path: ROUTES.USER_PROFILE,
				lazy: lazyView(() => import('../pages/profile')),
			},
			{
				id: 'annualDeclaration',
				path: ROUTES.ANNUAL_DECLARATION,
				lazy: lazyView(
					() => import('@/features/declarations-new/views/AnnualDeclarationEntry'),
				),
			},
			...fiscalAdvisorRoutes,
			//? `/invoice/*` era el flujo legacy sobre el NestJS. Ya no se sirve: solo
			//? redirige a la facturación (`LegacyInvoicingRedirect`).
			{ id: 'invoicing', path: ROUTES.INVOICING, element: <LegacyInvoicingRedirect /> },
			{ id: 'invoicing-detail', path: ROUTES.INVOICE_DETAIL, element: <LegacyInvoicingRedirect /> },
			//? FACTURACIÓN: el flujo sobre heru-fiscal-engine (Go), el único.
			{
				id: 'facturacion',
				path: ROUTES.FACTURACION,
				element: <InvoicingLayout />,
				handle: { pageWidth: 'full' },
				children: [
					{
						id: 'facturacion-root',
						path: ROUTES.FACTURACION,
						lazy: async () => ({
							Component: (
								await import('../features/invoicing/v2/views/InvoicingRoot')
							).InvoicingRoot,
						}),
					},
					...invoicingV2Routes,
				],
			},
			{
				id: 'taxProfile',
				path: ROUTES.TAX_PROFILE,
				lazy: lazyView(() => import('../pages/taxProfile')),
			},
			{
				id: 'shopping',
				path: ROUTES.SHOPPING,
				lazy: lazyView(() => import('../pages/shopping')),
			},
			{
				id: 'accountHeru',
				path: ROUTES.ACCOUNT_HERU,
				lazy: lazyView(() => import('../pages/accountHeru')),
			},
			{
				id: 'paymentMethods',
				path: ROUTES.PAYMENT_METHODS,
				lazy: lazyView(() => import('../pages/paymentMethods')),
			},
			{
				id: 'rappiCertificates',
				path: ROUTES.RAPPI_CERTIFICATES,
				handle: { pageWidth: 'full' },
				lazy: lazyView(() => import('@/features/platforms/views/CertificatesRoute')),
			},
			{
				id: 'zubaleCertificates',
				path: ROUTES.ZUBALE_CERTIFICATES,
				handle: { pageWidth: 'full' },
				lazy: lazyView(() => import('@/features/platforms/views/CertificatesRoute')),
			},
			{
				id: 'allyHub',
				path: ROUTES.ALLY_HUB,
				handle: { pageWidth: 'full' },
				lazy: lazyView(() => import('@/features/platforms/views/AllyHubView')),
			},
			{
				id: 'zubaleEnrollment',
				path: ROUTES.ZUBALE_ENROLLMENT,
				handle: { pageWidth: 'full' },
				lazy: lazyView(() => import('@/features/platforms/views/ZubaleEnrollmentView')),
			},
			{
				id: 'generalCertificates',
				path: `${ROUTES.GENERAL_CERTIFICATES}:certificateId?`,
				handle: { pageWidth: 'full' },
				lazy: lazyView(() => import('@/features/platforms/views/CertificatesRoute')),
			},
			{
				id: 'pendingReferences',
				path: ROUTES.PENDING_REFERENCES,
				lazy: lazyView(() => import('../pages/pendingReferences')),
			},
			{
				id: 'subscriptionDetail',
				path: ROUTES.SUBSCRIPTION_DETAIL,
				lazy: lazyView(() => import('../pages/subscriptionDetail')),
			},
			{
				id: 'plansAndPricesRoot',
				path: ROUTES.PLANS_AND_PRICES,
				loader: planSectionLoader,
			},
			{
				id: 'plansAndPricesSection',
				path: ROUTES.PLANS_SECTION,
				loader: planSectionLoader,
				lazy: lazyView(() => import('../pages/plansAndPricesV2')),
			},
			onboardingV3Routes,
			{
				id: 'successful-purchase',
				path: ROUTES.SUCCESSFUL_PURCHASE,
				lazy: lazyView(
					() => import('../pages/SuccessfulPurchase/SuccessfulPurchase'),
				),
			},
			{
				id: 'organizationSuccessLink',
				path: ROUTES.ORGANIZATION_SUCCESS_LINK,
				lazy: lazyView(() => import('../pages/OrganizationSuccessLink')),
			},
			{
				id: 'support',
				path: ROUTES.SUPPORT,
				lazy: lazyView(() => import('../pages/supportFormPage')),
				handle: { pageWidth: 'full' },
			},
			{
				id: 'supportForm',
				path: ROUTES.SUPPORT_FORM,
				lazy: lazyView(() => import('../pages/supportFormPage')),
				handle: { pageWidth: 'full' },
			},
			{
				id: 'newImsRegulations',
				path: ROUTES.NEW_IMS_REGULATIONS,
				lazy: lazyView(() => import('../pages/newImssRegulations')),
			},
			...declarationsRoutes,
			{
				id: 'taxMailbox',
				path: ROUTES.TAX_MAILBOX,
				lazy: lazyView(
					() => import('@/features/tax-mailbox/views/TaxMailboxView'),
				),
			},
			{
				id: 'satInbox',
				path: ROUTES.SAT_INBOX,
				lazy: lazyView(() => import('@/features/sat-inbox/SatInboxPage')),
			},
			],
	},
	satIdRoutes,
	{
		id: 'login',
		path: ROUTES.LOGIN,
		hydrateFallbackElement: <BootSplash />,
		lazy: async () => {
			const { default: LoginV2 } = await import('../pages/auth/LoginV2/index');
			return {
				element: (
					<NuqsAdapter>
						<LoginV2 />
					</NuqsAdapter>
				),
			};
		},
	},
	{
		id: 'paymentLinkResult',
		path: ROUTES.PAYMENT_LINK_RESULT,
		hydrateFallbackElement: <BootSplash />,
		lazy: lazyView(
			() => import('../features/payment-link/PaymentLinkResultPage'),
		),
	},
	{
		id: 'legacyOnboardingV2',
		path: ROUTES.LEGACY_ONBOARDING_V2,
		loader: legacyAllyDeeplinkLoader,
	},
	{
		id: 'calcumxCampaign',
		path: ROUTES.CALCUMX_CAMPAIGN,
		hydrateFallbackElement: <BootSplash />,
		lazy: async () => {
			const { default: PartnerCampaignRedirect } = await import(
				'../features/onboarding/PartnerCampaignRedirect'
			);
			return { element: <PartnerCampaignRedirect allyId='calcumx' /> };
		},
	},
	{
		id: 'tualCampaign',
		path: ROUTES.TUAL_CAMPAIGN,
		hydrateFallbackElement: <BootSplash />,
		lazy: async () => {
			const { default: PartnerCampaignRedirect } = await import(
				'../features/onboarding/PartnerCampaignRedirect'
			);
			return { element: <PartnerCampaignRedirect allyId='tual' /> };
		},
	},
	{
		id: 'plataCampaign',
		path: ROUTES.PLATA_CAMPAIGN,
		hydrateFallbackElement: <BootSplash />,
		lazy: async () => {
			const { default: PartnerCampaignRedirect } = await import(
				'../features/onboarding/PartnerCampaignRedirect'
			);
			return { element: <PartnerCampaignRedirect allyId='plata' /> };
		},
	},
	{
		id: 'maslowCampaign',
		path: ROUTES.MASLOW_CAMPAIGN,
		hydrateFallbackElement: <BootSplash />,
		lazy: async () => {
			const { default: PartnerCampaignRedirect } = await import(
				'../features/onboarding/PartnerCampaignRedirect'
			);
			return { element: <PartnerCampaignRedirect allyId='maslow' /> };
		},
	},
	{
		id: 'referralCapture',
		path: ROUTES.REFERRAL_CAPTURE,
		hydrateFallbackElement: <BootSplash />,
		lazy: lazyView(
			() => import('../features/referrals/ReferralCaptureRedirect'),
		),
	},
	{
		id: 'bannerPreview',
		path: ROUTES.BANNER_PREVIEW,
		lazy: lazyView(() => import('../features/banners/views/BannerPreviewView')),
	},
	...publicQrRoutes,
	...devTestRoutes,
]);

const Navigation = () => {
	return <RouterProvider router={router} />;
};

export default Navigation;
